import React, { useCallback, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { isFuture, isValid } from 'date-fns'

import { usePathParam, useViewport } from '@chilecompra/react-kit'
import { Button, Dialog, LinkButton, Typography } from '@chilecompra/react-kit/components'

import { PUBLIC_URL } from '../../config/settings/environment'
import { PATH_PARAM } from '../../config/settings/regexes'
import routes from '../../config/settings/routes'
import { USER_TYPE } from '../../config/settings/constant'

import {
  onGetAllSanctionsThunk,
  onGetContractualBehaviorSummaryThunk,
  onGetSanctionsByProcessTypeThunk
} from '../ContractualBehavior/ContractualBehavior.actions'
import { onCloseFeedbackDialogThunk, onUploadSanctionAttachmentThunk } from './Sanction.actions'

import { Column, Strong, Wrapper } from './SanctionFooter.styles'
import { DialogContent, DialogIcon, DialogMessage } from './Dialogs/SanctionDialog.styles'

import useReCaptcha from '../../modules/hooks/reCaptcha'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'

/**
 * The Sanction Footer's container.
 */
const SanctionFooter = () => {
  const { userType } = useAuthorization()
  const { executeReCaptcha } = useReCaptcha()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    size: { isUpToMedium }
  } = useViewport()
  const dni = usePathParam('rut')

  const {
    loading,
    error,
    success,
    showFeedbackDialog,
    form: {
      attachment: { file },
      backupDate,
      reason,
      resolutionNumber
    },
    poBiddingInfo: { error: poBiddingError, loading: poBiddingLoading, origin },
    userInfo: { error: userInfoError }
  } = useSelector(state => state.sanction)
  const { loadingAllSanctions } = useSelector(state => state.contractualBehavior)
  const { error: sellerError, orgLegalName } = useSelector(state => state.basicSellerInformation)

  const [showDialog, setShowDialog] = useState(false)

  const handleSubmit = () => {
    dispatch(
      onUploadSanctionAttachmentThunk({
        dni,
        callback: () => setShowDialog(false)
      })
    )
  }

  const handleDialogClose = () => {
    if (!loading) {
      setShowDialog(false)
    }
  }

  const handleDialogShow = () => () => setShowDialog(true)

  // Used for success and error modal
  const handleFeedbackDialogClose = () => {
    if (!success) {
      return dispatch(onCloseFeedbackDialogThunk())
    }
    batch(() => {
      dispatch(onCloseFeedbackDialogThunk())
      if (document.getElementById('recaptcha') && userType === USER_TYPE.PUBLIC) {
        const executeAll = async () => {
          await executeReCaptcha({ action: 'publicInfo' })
          dispatch(onGetContractualBehaviorSummaryThunk({ dni }))
        }

        executeAll()
      } else {
        dispatch(onGetContractualBehaviorSummaryThunk({ dni }))
      }
      if (loadingAllSanctions) {
        if (document.getElementById('recaptcha') && userType === USER_TYPE.PUBLIC) {
          const executeAll = async () => {
            await executeReCaptcha({ action: 'publicInfo' })
            dispatch(onGetAllSanctionsThunk({ dni }))
          }

          executeAll()
        } else {
          dispatch(onGetAllSanctionsThunk({ dni }))
        }
      } else {
        if (document.getElementById('recaptcha') && userType === USER_TYPE.PUBLIC) {
          const executeAll = async () => {
            await executeReCaptcha({ action: 'publicInfo' })
            dispatch(onGetSanctionsByProcessTypeThunk({ dni, processType: origin }))
          }

          executeAll()
        } else {
          dispatch(onGetSanctionsByProcessTypeThunk({ dni, processType: origin }))
        }
      }
    })
    navigate(routes.contractualBehavior.replace(PATH_PARAM, dni))
  }

  const disableSubmit = useCallback(() => {
    return (
      backupDate !== '' &&
      isValid(backupDate) &&
      !isFuture(backupDate) &&
      reason !== '' &&
      resolutionNumber !== '' &&
      !(file === null || file === undefined)
    )
  }, [file, backupDate, reason, resolutionNumber])

  return (
    <>
      <Column item xs={12} sm={12} md={9} padding={isUpToMedium ? '0 27px 0 0' : '0'}>
        <Wrapper>
          <LinkButton color="default" to={routes.contractualBehavior.replace(PATH_PARAM, dni)} variant="outlined">
            Volver
          </LinkButton>

          {!userInfoError && !poBiddingError && !poBiddingLoading && !sellerError && (
            <Button color="error" disabled={!disableSubmit()} variant="contained" onClick={handleDialogShow()}>
              Publicar sanción
            </Button>
          )}
        </Wrapper>
      </Column>

      {/* Confirmation Modal */}
      <Dialog maxWidth="430px" open={showDialog} onClose={handleDialogClose}>
        <DialogContent>
          <DialogIcon alt="Cambios guardados" src={`${PUBLIC_URL}/assets/images/circle-warning-icon.svg`} />
          <DialogMessage>
            <Typography fontWeight="bold" variant="h4">
              {`Estás a punto de publicar una sanción para el proveedor ${orgLegalName}`}
            </Typography>
            <Typography margin="16px 0 0" variant="body2">
              Una vez publicada, la sanción quedará visible en la ficha del proveedor para todos los compradores
              públicos, y afectará directamente su nota de comportamiento contractual.
            </Typography>
            <Button
              color="error"
              disabled={loading}
              loading={loading}
              margin="28px 0 0"
              variant="contained"
              onClick={handleSubmit}
            >
              Publicar sanción
            </Button>
          </DialogMessage>
        </DialogContent>
      </Dialog>

      {/* Feedback Modal */}
      {showFeedbackDialog && (
        <Dialog maxWidth="430px" open={showFeedbackDialog} onClose={handleFeedbackDialogClose}>
          <DialogContent>
            {success && (
              <DialogIcon alt="Sanción registrada" src={`${PUBLIC_URL}/assets/images/circle-success-icon.svg`} />
            )}
            <DialogMessage>
              <Typography fontWeight="bold" variant="h4">
                {error &&
                  error?.reason === 'COULD_NOT_UPLOAD_FILE_ERROR' &&
                  'Lo sentimos, el documento no pudo ser guardado'}
                {error &&
                  (error?.reason === 'SOMETHING_WENT_WRONG_ERROR' || error?.reason === 'PO_HAS_CONTRACT_ERROR') &&
                  'Lo sentimos, la sanción no pudo ser ingresada'}
                {success && 'Sanción registrada correctamente'}
              </Typography>
              <Typography margin="16px 0 0" variant="body2">
                {error && error?.reason !== 'PO_HAS_CONTRACT_ERROR' && 'Por favor, inténtalo en unos minutos más.'}
                {error && error?.reason === 'PO_HAS_CONTRACT_ERROR' && error?.originalError?.message}
                {success &&
                  'La sanción ha quedado visible en la ficha del proveedor. La nota del comportamiento del proveedor será modificada en un plazo máximo de '}
                {success && <Strong>5 días hábiles.</Strong>}
              </Typography>
              <Button color="default" margin="28px 0 0" variant="contained" onClick={handleFeedbackDialogClose}>
                {success && 'Aceptar'}
                {error && 'Volver'}
              </Button>
            </DialogMessage>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default SanctionFooter
